<template>
  <!-- eslint-disable prefer-standard-components/no-vuetify-inputs -->
  <v-col
    v-if="enabled"
    :aria-label="$bct('Enter a date of birth')"
    :cols="cols"
    :lg="lg"
    :md="md"
  >
    <v-row
      v-if="message"
      dense
    >
      <v-col
        class="labeled-input"
        cols="12"
      >
        <span>{{ $bct(label) }}</span>
        <RequiredIndicator v-if="mandatory" />
        <HelpIcon
          :document="helpDocument"
          :on-help-click="openHelpFlyout"
        />
      </v-col>
      <template v-if="description">
        <v-col
          class="fs-16 c-light-black my-1"
          cols="12"
        >
          <span>{{ $bct(description) }}</span>
        </v-col>
      </template>
    </v-row>
    <v-row dense>
      <v-col
        cols="12"
        lg="4"
        md="4"
      >
        <v-select
          v-model="month"
          :append-inner-icon="locked ? 'lock' : undefined"
          :aria-label="birthMonthAriaLabel"
          :density="dense || veryDense ? 'compact' : undefined"
          :disabled="locked"
          :item-title="(i) => $bct(i.text)"
          :items="monthOptions"
          :label="$bct('Birth month')"
          :readonly="readonly"
          color="primary"
          data-cy="birth_month"
          data-testid="birth_month"
          item-value="value"
          variant="filled"
          hide-details
          round
          tile
        />
      </v-col>
      <v-col
        cols="12"
        lg="4"
        md="4"
      >
        <v-select
          v-model="day"
          :append-inner-icon="locked ? 'lock' : undefined"
          :aria-label="birthDayAriaLabel"
          :density="dense || veryDense ? 'compact' : undefined"
          :disabled="locked"
          :item-title="(i) => $bct(i.text)"
          :items="dayOptions"
          :label="$bct('Birth day')"
          :readonly="readonly"
          color="primary"
          data-cy="birth_day"
          data-testid="birth_day"
          variant="filled"
          hide-details
          round
          tile
        />
      </v-col>
      <v-col
        cols="12"
        lg="4"
        md="4"
      >
        <v-select
          v-model="year"
          :append-inner-icon="locked ? 'lock' : undefined"
          :aria-label="birthYearAriaLabel"
          :density="dense || veryDense ? 'compact' : undefined"
          :disabled="locked"
          :item-title="(i) => $bct(i.text)"
          :items="yearOptions"
          :label="$bct('Birth year')"
          :readonly="readonly"
          color="primary"
          data-cy="birth_year"
          data-testid="birth_year"
          variant="filled"
          hide-details
          round
          tile
        />
      </v-col>
    </v-row>
  </v-col>
</template>

<script setup>
import DateOptions from '@/shared/assets/date-options';
import HelpIcon from '@/shared/components/form/HelpIcon.vue';
import propsToRefs from '@/shared/utils/propsToRefs';
import RequiredIndicator from '@/shared/components/RequiredIndicator.vue';
import useLabeledField from '@/shared/composables/useLabeledField';
import useSchematizedField from '@/shared/composables/useSchematizedField';

const $bct = inject('translator');

const props = defineProps({
  readonly: {
    type: Boolean,
    default: false,
  },
  ...useLabeledField.props,
  ...useSchematizedField.props,
});

defineEmits(useLabeledField.emits);

const localValue = defineModel({ type: String });

const { handleUpdateModelValue, handleBlur } = useLabeledField(
  ...propsToRefs(props, useLabeledField.paramKeys),
);

const { enabled, helpDocument, label, locked, openHelpFlyout } = useSchematizedField(
  ...propsToRefs(props, useSchematizedField.paramKeys),
);

const month = ref(null);
const monthOptions = ref(DateOptions.month_options);
const day = ref(null);
const dayOptions = ref(DateOptions.month_day_options);
const year = ref(null);
const yearOptions = ref([]);

const birthMonthAriaLabel = computed(() => {
  const base = $bct('Birth month');
  const selectedMonth = monthOptions.value.find((m) => m.value === month.value);
  const monthText = selectedMonth ? $bct(selectedMonth.text) : '';

  if (!props.mandatory) return monthText ? `${base}: ${monthText}` : base;

  return monthText ? `${base} - ${monthText} - required` : `${base} - required`;
});

const birthDayAriaLabel = computed(() => {
  const base = $bct('Birth day');
  const selectedDay = dayOptions.value.find((dayOption) => dayOption.value === day.value);
  const dayText = selectedDay ? $bct(selectedDay.text) : '';
  if (!props.mandatory) return dayText ? `${base}: ${dayText}` : base;

  return dayText ? `${base} - ${dayText} - required` : `${base} - required`;
});

const birthYearAriaLabel = computed(() => {
  const base = $bct('Birth year');
  const selectedYear = yearOptions.value.find((y) => y.value === year.value);
  const yearText = selectedYear ? $bct(selectedYear.text) : '';
  if (!props.mandatory) return yearText ? `${base}: ${yearText}` : base;

  return yearText ? `${base} - ${yearText} - required` : `${base} - required`;
});

const combine = () => {
  if (year.value && month.value && day.value) {
    const newValue = [year.value, month.value, day.value].join('-');
    if (newValue === localValue.value) return;

    handleUpdateModelValue(newValue);
    handleBlur(newValue);
  }
};

const generateYearOptions = () => {
  let currentYear;
  if (props.propMaximum) {
    currentYear = new Date(props.propMaximum).getFullYear();
  } else {
    currentYear = new Date().getFullYear();
  }

  let lastYear;
  if (props.propMinimum) {
    lastYear = new Date(props.propMinimum).getFullYear();
  } else {
    lastYear = currentYear - 95;
  }

  const years = [];
  for (let i = currentYear; i >= lastYear; i -= 1) {
    years.push({ text: i.toString(), value: i.toString() });
  }
  return years;
};

const split = () => {
  if (!localValue.value) {
    month.value = null;
    day.value = null;
    year.value = null;
  } else if (localValue.value.includes('/')) {
    const splitDob = localValue.value.split('/');
    month.value = splitDob[0].padStart(2, '0');
    day.value = splitDob[1].padStart(2, '0');
    year.value = splitDob[2];
  } else {
    const splitDob = localValue.value.split('-');
    month.value = splitDob[1].padStart(2, '0');
    day.value = splitDob[2].padStart(2, '0');
    year.value = splitDob[0];
  }
};

watch(
  () => localValue.value,
  () => {
    split();
  },
);

watch([day, month, year], () => {
  combine();
});

onMounted(() => {
  split();
  yearOptions.value = generateYearOptions();
});
</script>
